import { ACCOUNTING_CA, docTypeOf, type IPack } from '../packs/index.js';

/** The facts a document's name and folder are built from. */
export interface IFilingFields {
  /** YYYY-MM-DD. */
  date?: string;
  /** Short name of a confirmed client, e.g. ABCCorp. */
  clientShort?: string;
  /** Full name of that client, e.g. ABC Corporation (used for the folder). */
  clientName?: string;
  /** A DOC_TYPES id. */
  docType?: string;
  /** Vendor, bank, period or number. Optional. */
  detail?: string;
  /** Fiscal or tax year; defaults to the date's year. */
  year?: number;
  /** The client's or matter's folder name, when it differs from clientName (law: "24-0153 - Smith, John"). */
  clientFolder?: string;
  /** Law: the matter's practice area folder. */
  area?: string;
  /** Immigration: the applicant the document is about, as read (its own folder under the matter). */
  applicant?: string;
}

export type NameStyle = 'underscores' | 'spaces';

export interface INamingRules {
  /** Order of the parts; Detail is optional and dropped when empty. */
  parts: ('date' | 'client' | 'docType' | 'detail')[];
  style: NameStyle;
  /** Folder levels under the library (or its filing root). */
  folders: FolderLevel[];
  /**
   * A different layout for some categories, e.g. payroll under Client › Payroll & HR › Year:
   * { 'Payroll & HR': ['client', 'category', 'year'] }.
   */
  byCategory?: { [category: string]: FolderLevel[] };
}

export type FolderLevel = 'client' | 'year' | 'category' | 'area' | 'applicant';

/** The standard: YYYY-MM-DD_Client_DocType_Detail, filed under Client/Year/Category. */
export const DEFAULT_RULES: INamingRules = {
  parts: ['date', 'client', 'docType', 'detail'],
  style: 'underscores',
  folders: ['client', 'year', 'category']
};

const MAX_BASE_LENGTH: number = 120;
const DATE: RegExp = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isValidDate(value: string | undefined): boolean {
  const m: RegExpMatchArray | null = (value ?? '').match(DATE);
  if (!m) {
    return false;
  }
  const d: Date = new Date(`${value}T00:00:00Z`);
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() + 1 === Number(m[2]) && d.getUTCDate() === Number(m[3]);
}

/** Characters SharePoint doesn't allow in names, plus # and % which break some links. */
const ILLEGAL: RegExp = /["*:<>?/\\|#%]/g;

/** "Ford Transit", "td bank", "2025-014" → FordTransit, TdBank, 2025-014 (keeps digits and inner hyphens). */
export function compactPart(text: string): string {
  return text
    .replace(ILLEGAL, ' ')
    .split(/[^A-Za-z0-9-]+/)
    .filter(w => w && w !== '-')
    .map(w => (/^[a-z]/.test(w) ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join('')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

/** A readable part for the spaces style: trimmed, single spaces, no illegal characters. */
function readablePart(text: string): string {
  return text.replace(ILLEGAL, ' ').replace(/\s+/g, ' ').trim().slice(0, 60);
}

/** A folder name SharePoint accepts: no illegal characters, no leading/trailing dots or spaces. */
export function safeFolderName(text: string): string {
  return text.replace(ILLEGAL, ' ').replace(/\s+/g, ' ').trim().replace(/^[.\s]+|[.\s]+$/g, '').slice(0, 100);
}

export interface IRendered {
  /** Full file name with extension, or undefined when a required part is missing. */
  name?: string;
  /** Folder path segments, e.g. ['ABC Corporation', '2025', 'Financial & Bank Statements']. */
  folders?: string[];
  /** Plain-language names of what's missing: 'date', 'client', 'document type'. */
  missing: string[];
}

/**
 * Builds the file name and destination folders from the fields — deterministic, so every name
 * follows the same standard and a reviewer only ever fixes a field.
 */
export function render(fields: IFilingFields, extension: string, rules: INamingRules = DEFAULT_RULES, pack: IPack = ACCOUNTING_CA): IRendered {
  const type = docTypeOf(pack, fields.docType);
  // The firm's own records (law: trust bank statements, ledgers…) belong to no client or matter.
  const organisation: boolean = type?.scope === 'organisation';
  const missing: string[] = [];
  if (!isValidDate(fields.date)) { missing.push('date'); }
  if (!organisation && (!fields.clientShort || !fields.clientName)) { missing.push(pack.entity.one); }
  if (!type) { missing.push('document type'); }
  if (missing.length > 0) {
    return { missing };
  }
  const underscores: boolean = rules.style === 'underscores';
  const pieces: string[] = [];
  for (const part of rules.parts) {
    let value: string = '';
    if (part === 'date') { value = fields.date!; }
    if (part === 'client' && !organisation) { value = underscores ? compactPart(fields.clientShort!) : readablePart(fields.clientName!); }
    if (part === 'docType') { value = underscores ? type!.id : type!.label; }
    if (part === 'detail' && fields.detail) { value = underscores ? compactPart(fields.detail) : readablePart(fields.detail); }
    if (value) { pieces.push(value); }
  }
  let base: string = underscores ? pieces.join('_') : pieces.join(' - ');
  base = base.slice(0, MAX_BASE_LENGTH).replace(/[_\s.-]+$/, '');
  const ext: string = extension.replace(/^\./, '').toLowerCase();
  const year: number = fields.year ?? Number(fields.date!.slice(0, 4));
  if (organisation) {
    const root: string[] = pack.organisationRoot ? [pack.organisationRoot] : [];
    return { name: ext ? `${base}.${ext}` : base, folders: [...root, String(year), type!.category], missing };
  }
  const levels: FolderLevel[] = rules.byCategory?.[type!.category] ?? rules.folders;
  const folders: string[] = levels
    .map(level => (level === 'client' ? safeFolderName(fields.clientFolder ?? fields.clientName!)
      : level === 'year' ? String(year) : level === 'area' ? safeFolderName(fields.area ?? '')
        : level === 'applicant' ? safeFolderName(fields.applicant ?? '') : type!.category))
    .filter(f => !!f);
  return { name: ext ? `${base}.${ext}` : base, folders: pack.entityRoot ? [pack.entityRoot, ...folders] : folders, missing };
}
